import {
  collection,
  getDocs,
  query,
  where,
  orderBy,
  limit,
  type DocumentData,
} from 'firebase/firestore';
import { db } from '../../config';
import { userRole, registerStatus } from '../../../../constants/enum';

/**
 * 担当者デフォルト値計算で利用する企業担当者の概要 #2642
 */
export interface CompanyRepresentative {
  uid: string;
  name: string;
  iconUrl: string | null;
}

/**
 * 企業担当者を取得 #2642
 *
 * 主クエリ: `companyAdmin` ロールで登録完了(finished)の最古登録ユーザー。
 * フォールバック: 同一会社の登録完了(finished)ユーザーで最古登録のもの。
 *
 * 代理アップロード時のデフォルト担当者を計算するために利用する。
 * 担当者候補リスト（finished のみ）と条件を揃え、返した代表者が必ず候補に
 * 含まれるようにする（招待中ユーザーを返すと一覧で「退会済ユーザー」表示になるため）#2856
 */
export const getCompanyRepresentative = async (
  companyId: string,
): Promise<CompanyRepresentative | null> => {
  const usersRef = collection(db, 'users');

  const adminQuery = query(
    usersRef,
    where('company_id', '==', companyId),
    where('role', '==', userRole.companyAdmin),
    where('register_status', '==', registerStatus.finished),
    orderBy('created_at', 'asc'),
    limit(1),
  );
  const adminSnap = await getDocs(adminQuery);
  if (!adminSnap.empty) {
    return toRepresentative(adminSnap.docs[0].id, adminSnap.docs[0].data());
  }

  // フォールバック: companyAdmin が居ない場合は同一会社の最古アクティブユーザー
  const fallbackQuery = query(
    usersRef,
    where('company_id', '==', companyId),
    where('register_status', '==', registerStatus.finished),
    orderBy('created_at', 'asc'),
    limit(1),
  );
  const fallbackSnap = await getDocs(fallbackQuery);
  if (!fallbackSnap.empty) {
    return toRepresentative(fallbackSnap.docs[0].id, fallbackSnap.docs[0].data());
  }

  return null;
};

const toRepresentative = (uid: string, data: DocumentData): CompanyRepresentative => ({
  uid,
  name: (data.name as string) ?? '',
  iconUrl: (data.icon_url as string | undefined) ?? null,
});
